import React, { useState, useEffect } from 'react';
import useSEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { settingsApi, Gallery } from '../api/settings';
import { Loader2, Image as ImageIcon } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';

const GalleryPage: React.FC = () => {
    useSEO({
        title: "Galerie - ISTMECH",
        description: "Découvrez la galerie de photos de l'Institut Supérieur de Technologie Médicale du Cap-Haïtien. Campus, événements, activités étudiantes.",
        keywords: "ISTMECH, galerie, photos, campus, événements, activités",
        url: "https://www.istmech.edu.ht/galerie",
        image: "https://www.istmech.edu.ht/og-image.png"
    });

    const [galleries, setGalleries] = useState<Gallery[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [categories, setCategories] = useState<string[]>([]);

    useEffect(() => {
        fetchGalleries();
    }, []);

    const fetchGalleries = async () => {
        try {
            const data = await settingsApi.getAllGalleries();
            setGalleries(data);

            // Extract unique categories
            const uniqueCategories = Array.from(new Set(data.map(item => item.category)));
            setCategories(uniqueCategories);
        } catch (error) {
            console.error("Failed to fetch galleries", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredGalleries = selectedCategory === 'all'
        ? galleries
        : galleries.filter(gallery => gallery.category === selectedCategory);

    const getCategoryLabel = (category: string) => {
        switch (category) {
            case 'general': return 'Général';
            case 'campus': return 'Campus';
            case 'events': return 'Événements';
            case 'students': return 'Étudiants';
            default: return category.charAt(0).toUpperCase() + category.slice(1);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
            <Header />
            <main className="flex-grow pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
                <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
                        Galerie <span className="text-blue-600 dark:text-blue-400">Photos</span>
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        Explorez la vie à l'ISTMECH à travers nos images. Découvrez notre campus, nos événements et nos moments forts.
                    </p>
                </div>

                {/* Filter Tabs */}
                {galleries.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-10 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${selectedCategory === 'all'
                                ? 'bg-blue-600 text-white shadow-md transform scale-105'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                        >
                            Tout voir
                        </button>
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${selectedCategory === category
                                    ? 'bg-blue-600 text-white shadow-md transform scale-105'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                {getCategoryLabel(category)}
                            </button>
                        ))}
                    </div>
                )}

                {/* Gallery Grid */}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
                    </div>
                ) : filteredGalleries.length === 0 ? (
                    <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in duration-500">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 mb-6">
                            <ImageIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Aucune image trouvée</h3>
                        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                            La galerie est vide pour le moment ou aucune image ne correspond à cette catégorie.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {filteredGalleries.map((gallery, index) => (
                            <div
                                key={gallery.id}
                                className="group relative overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in"
                                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'both' }}
                            >
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <div className="aspect-[4/3] overflow-hidden relative cursor-pointer">
                                            <div className="absolute inset-0 bg-gray-200 dark:bg-gray-800 animate-pulse" /> {/* Placeholder */}
                                            <img
                                                src={gallery.imageUrl}
                                                alt={gallery.title}
                                                loading="lazy"
                                                className="h-full w-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                                                onLoad={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.previousElementSibling?.remove(); // Remove placeholder
                                                }}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=No+Image';
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                                                <Badge className="self-start mb-2 bg-blue-600 text-white border-none">
                                                    {getCategoryLabel(gallery.category)}
                                                </Badge>
                                                <h3 className="text-white font-bold text-lg leading-tight transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                                    {gallery.title}
                                                </h3>
                                            </div>
                                        </div>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-4xl w-full p-0 overflow-hidden bg-transparent border-none shadow-none z-50">
                                        <div className="relative bg-black/90 rounded-lg overflow-hidden flex flex-col md:flex-row max-h-[90vh]">
                                            <div className="flex-1 flex items-center justify-center bg-black p-4">
                                                <img
                                                    src={gallery.imageUrl}
                                                    alt={gallery.title}
                                                    className="max-h-[80vh] w-auto max-w-full object-contain"
                                                />
                                            </div>
                                            <div className="w-full md:w-80 bg-white dark:bg-gray-900 p-6 flex flex-col overflow-y-auto">
                                                <Badge className="self-start mb-4 bg-blue-600 text-white border-none">
                                                    {getCategoryLabel(gallery.category)}
                                                </Badge>
                                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                                    {gallery.title}
                                                </h2>
                                                {gallery.description ? (
                                                    <div className="prose dark:prose-invert text-sm text-gray-600 dark:text-gray-300 break-words whitespace-pre-wrap">
                                                        <p>{gallery.description}</p>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm italic text-muted-foreground">Aucune description disponible.</p>
                                                )}
                                                <div className="mt-auto pt-6 text-xs text-muted-foreground border-t dark:border-gray-800">
                                                    Ajouté le {new Date(gallery.createdAt || new Date()).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        ))}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default GalleryPage;
